<template>
  <div class="h-full">
    <TextBlob headingSize="l">
      <template #heading>
        <AnimatedBackdrop class="py-8 relative m-6">
          <div class="bg-black bg-opacity-50 p-4 rounded-md mx-auto w-4/5">
            <img :src="HeroGrowLogo" alt="logo" class="w-1/2 mx-auto" />
            <div class="text-center text-[3rem] my-8 leading-relaxed">
              Review Automation and<br />
              <span class="text-orange-500">Insightful Metrics</span> <br />
              for
              <span class="text-orange-500">Your Firm</span>
            </div>
            <button
              class="glow-button mx-auto block text-white bg-orange-500 hover:bg-orange-600 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 text-center"
              @click="scrollToElement('form')"
            >
              Request a Demo
            </button>
          </div>
        </AnimatedBackdrop>
        <div class="non-linear-gradient"></div>
      </template>
      <template #text>
        <div class="w-full h-fit my-4">
          <div class="flex justify-between items-center">
            <div class="flex-1"><!--empty for spacing--></div>
            <div class="text-4xl text-center flex-1">
              Who are <span class="text-orange-500">We</span>?
            </div>
            <div class="flex flex-1 justify-end">
              <router-link to="/login">
                <el-button class="bht" type="success" size="large" plain>
                  ReviewIQ Login
                </el-button>
              </router-link>
            </div>
          </div>
          <div class="text-xl my-8 mx-4 md:mx-40 space-y-6">
            <ReactiveCard>
              <div id="futurecard1" class="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div class="md:p-8">
                  HeroGrow, a business intelligence company, is a   
                  team of professionals dedicated to helping businesses grow through the power of online
                  reviews and insightful analytics.
                </div>
                <div class="flex justify-center">
                  <div
                    class="menu-button flex flex-col items-center cursor-pointer min-w-[130px] rounded px-8 py-4 bg-slate-700 hover:bg-gray-600 hover:text-white transition-all duration-500"
                    @click="scrollToElement('team')"
                  >
                    <Icon icon="mdi:account-group-outline" height="3em" />
                    <div>Team</div>
                  </div>
                </div>
              </div>
            </ReactiveCard>
            
            <ReactiveCard>
              <div id="futurecard2" class="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                <div class="flex justify-center order-2 md:order-1">
                  <div
                    class="menu-button flex flex-col items-center cursor-pointer min-w-[130px] rounded px-8 py-4 bg-slate-700 hover:bg-gray-600 hover:text-white transition-all duration-500"
                    @click="scrollToElement('reviewiq')"
                  >
                    <Icon icon="mdi:message-draw" height="3em" />
                    <div>ReviewIQ</div>
                  </div>
                </div>
                <div class="order-1 md:order-2 md:p-8">
                  Track your clients' reviews and invoice payments, monitor your online
                  reputation, and gain valuable insights into your business with our ever evolving suite of tools.
                </div>
              </div>
            </ReactiveCard>
            <!-- For a complete overview of our services, please
            <span
              @click="scrollToElement('services')"
              class="cursor-pointer text-orange-500"
              >see below.</span
            > -->
          </div>
        </div>
        <SplashText class="hidden" />
        <div class="text-4xl text-center my-8">
          Your <span class="text-orange-500">Review Flow</span>
        </div>
        <StepsSection />
        <RequestForm id="form" class="my-20" />
        <TeamSection id="team" />
        <!-- <ServicesSection id="services" /> -->
        <ReviewIQIntro id="reviewiq" />
        <ContactUsButton class="my-20" />
        <PricingCards class="hidden" />
      </template>
    </TextBlob>
    <button
      v-if="showButton"
      @click="scrollTop"
      class="fixed bottom-20 right-8 animate-bounce bg-gray-900 text-white p-2 px-4 rounded-full cursor-pointer transition-opacity opacity-80 hover:opacity-100 z-10"
    >
      <Icon icon="mdi:chevron-up" height="3em" />
    </button>
  </div>
</template>

<script setup>
import TextBlob from "@/components/TextBlob.vue";
import AnimatedBackdrop from "@/components/AnimatedBackdrop.vue";
import ReactiveCard from "@/components/ReactiveCard.vue";
import SplashText from "@/views/HomeView/SplashText.vue";
import PricingCards from "@/views/HomeView/PricingCards.vue";
import ContactUsButton from "@/views/HomeView/components/ContactUsButton.vue";
import TeamSection from "@/views/HomeView/TeamSection.vue";
import ServicesSection from "@/views/HomeView/ServicesSection.vue";
import StepsSection from "@/views/HomeView/StepsSection.vue";
import ReviewIQIntro from "@/views/HomeView/ReviewIQIntro.vue";
import MenuItems from "@/views/HomeView/MenuItems.vue";
import RequestForm from "@/views/HomeView/RequestForm.vue";
import { Icon } from "@iconify/vue";
import HeroGrowLogo from "@/assets/herogrowlogo.png";
import { useDump } from "@/stores";
const { scrollToElement } = useDump();

import { useUser } from "@/stores";
import { ref } from "vue";

import { onMounted, onUnmounted } from "vue";

onMounted(() => {
  window.addEventListener("scroll", handleScroll);
});

onUnmounted(() => {
  window.removeEventListener("scroll", handleScroll);
});

const showButton = ref(false);

const scrollTop = () => {
  window.scrollTo({
    top: 0,
    behavior: "smooth",
  });
};

const handleScroll = () => {
  const shouldShow = window.scrollY > 200;
  if (shouldShow !== showButton.value) {
    showButton.value = shouldShow;
  }
};

const store = useUser();
store.activeRoute = "login";
</script>

<style scoped>
html {
  scroll-behavior: smooth;
}

.call-font {
  font-family: "Optima", sans-serif;
  font-size: 2em;
}

.animate-bounce {
  animation-duration: 5s;
}

.non-linear-gradient {
  background: linear-gradient(
    to bottom,
    black,
    rgba(0, 0, 0, 0.8) 25%,
    rgba(0, 0, 0, 0) 100%
  );
  height: 20px;
}

.shadow-effect {
  box-shadow: 2px 4px 10px rgba(0, 0, 0, 0.5); /* Adjust the color and spread to your liking */
}

.glow-button {
  position: relative;
  transition: background-color 0.5s ease-in-out;
  box-shadow: 0 0 10px 5px rgba(255, 165, 0, 0.6);
}

/* The glow pulse animates only opacity on a pseudo-element (compositable)
   instead of animating box-shadow, which forces a repaint every frame. */
.glow-button::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  box-shadow: 0 0 20px 10px rgba(255, 165, 0, 0.8);
  opacity: 0;
  animation: pulseGlow 3s infinite;
  pointer-events: none;
}

@keyframes pulseGlow {
  0%, 100% {
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
}
</style>
